import React from 'react';
import styles from '../../../../../../styles/SettingsTab.module.css';

interface GeneralSettingsProps {
  formData: {
    name: string;
    description: string;
    theme_color: string;
  };
  logoPreview: string | null;
  bannerPreview: string | null;
  logoFile: File | null;
  bannerFile: File | null;
  onInputChange: (field: string, value: any) => void;
  onColorChange: (color: string) => void;
  onFileUpload: (file: File, type: 'logo' | 'banner') => void;
  onRemoveFile: (type: 'logo' | 'banner') => void;
  formatFileSize: (bytes: number) => string;
}

const presetColors = [
  { color: '#1a237e', name: 'Deep Blue' },
  { color: '#2563eb', name: 'Blue' },
  { color: '#7c3aed', name: 'Purple' },
  { color: '#dc2626', name: 'Red' },
  { color: '#ea580c', name: 'Orange' },
  { color: '#059669', name: 'Green' },
  { color: '#0891b2', name: 'Cyan' },
  { color: '#be185d', name: 'Pink' }
];

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  formData,
  logoPreview,
  bannerPreview,
  logoFile,
  bannerFile,
  onInputChange,
  onColorChange,
  onFileUpload,
  onRemoveFile,
  formatFileSize
}) => {
  return (
    <div className={styles.settingsSections}>
      <div className={styles.settingsSection}>
        <span className={styles.sectionBadge}>Basic</span>
        <h3 className={styles.sectionTitle}>
          <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
          General Settings
        </h3>
        
        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="groupName">Group Name</label>
          <input
            type="text"
            className={styles.formControl}
            id="groupName"
            value={formData.name}
            onChange={(e) => onInputChange('name', e.target.value)}
            placeholder="Enter group name"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="groupDescription">Description</label>
          <textarea
            className={`${styles.formControl} ${styles.formTextarea}`}
            id="groupDescription"
            value={formData.description}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="Enter group description"
          />
        </div>
      </div>

      <div className={styles.settingsSection}>
        <span className={styles.sectionBadge}>Appearance</span>
        <h3 className={styles.sectionTitle}>
          <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
          </svg>
          Group Appearance
        </h3>

        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="groupColor">Theme Color</label>
          <div className={styles.colorPickerContainer}>
            <div className={styles.colorPickerWrapper}>
              <div
                className={styles.colorPreview}
                style={{ backgroundColor: formData.theme_color }}
                onClick={() => document.getElementById('groupColor')?.click()}
              >
                <input
                  type="color"
                  className={styles.colorPickerInput}
                  id="groupColor"
                  value={formData.theme_color}
                  onChange={(e) => onColorChange(e.target.value)}
                />
              </div>
              <div className={styles.colorInfo}>
                <div className={styles.colorName}>Primary Theme Color</div>
                <div className={styles.colorValue}>{formData.theme_color}</div>
              </div>
            </div>
            <div className={styles.colorPalette}>
              {presetColors.map((preset) => (
                <div
                  key={preset.color}
                  className={`${styles.presetColor} ${formData.theme_color === preset.color ? styles.active : ''}`}
                  style={{ backgroundColor: preset.color }}
                  title={preset.name}
                  onClick={() => onColorChange(preset.color)}
                />
              ))}
            </div>
          </div>
          <div className={styles.formHelp}>Choose a color that represents your group's identity</div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Group Logo</label>
          <div className={styles.fileUploader}>
            {!logoPreview ? (
              <div
                className={styles.uploadArea}
                onClick={() => document.getElementById('groupLogo')?.click()}
              >
                <div className={styles.uploadIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17,8 12,3 7,8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </div>
                <div className={styles.uploadText}>
                  <div className={styles.uploadTitle}>Click to upload logo</div>
                  <div className={styles.uploadSubtitle}>PNG, JPG, GIF up to 5MB</div>
                </div>
              </div>
            ) : (
              <div className={styles.filePreview}>
                <img src={logoPreview} alt="Logo preview" />
                <div className={styles.fileInfo}>
                  <div className={styles.fileName}>{logoFile?.name}</div>
                  <div className={styles.fileSize}>{logoFile ? formatFileSize(logoFile.size) : ''}</div>
                </div>
                <button
                  type="button"
                  className={styles.removeFile}
                  onClick={() => onRemoveFile('logo')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            )}
            <input
              type="file"
              id="groupLogo"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files?.[0] && onFileUpload(e.target.files[0], 'logo')}
            />
          </div>
          <div className={styles.formHelp}>Recommended size: 200x200px or larger</div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Banner Image</label>
          <div className={styles.fileUploader}>
            {!bannerPreview ? (
              <div
                className={styles.uploadArea}
                onClick={() => document.getElementById('groupBanner')?.click()}
              >
                <div className={styles.uploadIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21,15 16,10 5,21"></polyline>
                  </svg>
                </div>
                <div className={styles.uploadText}>
                  <div className={styles.uploadTitle}>Click to upload banner</div>
                  <div className={styles.uploadSubtitle}>PNG, JPG, GIF up to 10MB</div>
                </div>
              </div>
            ) : (
              <div className={styles.filePreview}>
                <img src={bannerPreview} alt="Banner preview" />
                <div className={styles.fileInfo}>
                  <div className={styles.fileName}>{bannerFile?.name}</div>
                  <div className={styles.fileSize}>{bannerFile ? formatFileSize(bannerFile.size) : ''}</div>
                </div>
                <button
                  type="button"
                  className={styles.removeFile}
                  onClick={() => onRemoveFile('banner')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            )}
            <input
              type="file"
              id="groupBanner"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files?.[0] && onFileUpload(e.target.files[0], 'banner')}
            />
          </div>
          <div className={styles.formHelp}>Recommended size: 1200x300px or larger</div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;

